import { ComponentLibrary } from '@suntex.classic/adobe-animate';
import type { DisplayObject } from '@suntex.classic/createjs';
import { PropertyPath } from '@suntex.core/animation';
import type { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';
import { GradeLevel } from '@suntex.fim/enums';

import { IconKey } from './assets';
import { ModuleSlug } from './const/db/fim-modules';
import { InfoPane } from './display/InfoPane';

export type { ModuleSlug } from './const/db/fim-modules';
export type {
  ModuleTag,
  ModuleTagging,
  ModuleSpecifier,
} from '@suntex.fim/framework';

export enum StarValue {
  None = 0,
  Silver = 1,
  Gold = 2,
}

/**
 * An optional array of module tag path prefixes
 */
export type ModuleFilter = readonly string[] | undefined;

export interface IHub {
  display(opts: {
    path: string;
    isModuleEnabled: (moduleSlug: string) => boolean;
    focus: ModuleFilter;
    gradeLevel: GradeLevel | undefined;
    progressSummary: Entities_Player_ProgressSummary_Response | undefined;
    settings: HubSettings | undefined;
    onPathChange?(path: string): void;
    setPath?: (path: string) => void;
    navigate?: (href: string) => void;
  }): void;
  getStarStats(): {
    goldStars: number;
    silverStars: number;
    totalStars: number;
  } | null;
  /**
   * Cleanup method to remove all radials and event listeners.
   * Call this when unmounting the hub component to prevent phantom interactions.
   */
  dispose(): void;
}

export interface HubSettings {
  /** Path the hub opens on when there is no URL hash to restore. */
  defaultPath?: string;
  catalogView?: boolean;
  alwaysShowLabels?: boolean;
  animationSpeed?: number;
  disableAnimation?: boolean;
  flatten?: boolean;
}

export interface SSInfoPane extends InfoPane {
  badge: DisplayObject;
  className: string;
  g1Info: GameInfo;
  g2Info: GameInfo;
  g3Info: GameInfo;
}

type GameInfo = {
  totalTime: TextFieldObject;
  infoLabels: DisplayObject;
  skillsText: { text: string };
};

export interface TextFieldObject extends DisplayObject {
  text?: string;
  font?: string;
  color?: string;
  lineHeight?: number;
  lineWidth?: number;
  textAlign?: string;
  textBaseline?: string;
}

export interface NodeModel {
  key?: string;
  tag?: string;
  alias?: string[];
  moduleSlug?: ModuleSlug;
  moduleVariant?: number;
  dataPath?: PropertyPath<Entities_Player_ProgressSummary_Response>;
  dataTarget?: number;
  href?: string;
  info?: {
    title?: string;
    summary?: string;
  };
  icon?: IconShorthand;
  hidden?: boolean;
  hideProgressBar?: boolean;
  label:
    | string
    | {
        aria: string;
        class?: string;
        frame?: number;
      };
  filtered?: {
    icon?: IconShorthand;
    label?: string;
    color?: number;
  };
  /**
   * If true, this radial duplicates another node of the tree, and should be omitted when filtering.
   */
  duplicate?: boolean;

  /**
   * Logic to use when determining if a radial with children is enabled or in focus.
   * 'OR': radial is in focus if ANY child is enabled or in focus (default)
   * 'AND': radial is in focus if ALL children are enabled or in focus
   */
  recursiveLogic?: 'OR' | 'AND';

  color?: number;
  layout?: RadialLayout;
  centerOffset?: { x: number; y: number };

  /**
   * Tag path for filtering modules from moduleTagging data.
   * When set, children are dynamically computed at display time by filtering
   * the modules node tree using moduleTagging entries matching this path.
   */
  filterTagPath?: string;
  /**
   * Reference to the root node to filter when filterTagPath is set.
   * Defaults to the modules node if not specified.
   */
  filterRoot?: NodeModel;

  children?: readonly NodeModel[];
}

export interface StrictNodeModel extends NodeModel {
  key: string;
  color: number;
  icon: Exclude<IconShorthand, string>;
  ariaLabel: string;

  children: readonly StrictNodeModel[];
}

export type RadialLayout =
  | 'anchor'
  | 'arc'
  | 'clock'
  | 'horizontal'
  | 'levels'
  | 'modes'
  | 'operations'
  | 'skill-set'
  | 'star'
  | 'vertical';

type IconShorthand =
  | IconKey
  | {
      image: IconKey;
      text?: undefined;
    }
  | {
      text: string;
      image?: undefined;
    };

export type BuilderDependencies = {
  getHref: (href?: string | null, preview?: boolean) => string | null;
  lib: ComponentLibrary;
};
